// rfqPortalService — the EXTERNAL supplier portal client (magic-link / guest).
//
// Every call carries the opaque `:token` in the path; the backend ShareTokenGuard
// resolves it to one RFQ + one supplier. No JWT / tenant headers needed — a
// logged-out supplier drives the whole flow from the link alone. Mirrors the
// authenticated buyerAgentService surface so the RFQ shell can run in guest mode.

import apiClient from './api';
import type { Workspace } from './workspaceService';

const P = '/buyer-agent/portal';

export interface PortalViewer {
  partnerId?: string;
  partnerName?: string;
  scope?: string;
}

export type PortalWorkspace = Workspace & { viewer?: PortalViewer };

export interface PortalQuoteDto {
  unitPrice: number;
  currency?: string;
  leadTimeDays?: number;
  validUntilDays?: number;
  setupCost?: number;
  toolingCost?: number;
  materialCost?: number;
  laborCost?: number;
  notes?: string;
}

export const rfqPortalService = {
  /** The collaboration workspace for this token. Opening it also stamps the
   *  supplier-viewed signal (server side); it does not change the RFQ status. */
  async getWorkspace(token: string): Promise<PortalWorkspace> {
    const res = await apiClient.get(`${P}/${token}/workspace`);
    return res.data?.data;
  },

  /** Supplier changes the RFQ state (reviewing / quoting / decline). */
  async setState(token: string, status: string, reason?: string): Promise<{ rfqId: string; status: string }> {
    const res = await apiClient.post(`${P}/${token}/state`, { status, reason });
    return res.data?.data;
  },

  /** Hand over the quotation as files. Nothing else is asked of the supplier. */
  async submitQuoteFiles(token: string, files: File[], note?: string): Promise<{ quoteId: string; revision: number; receivedAt: string }> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    if (note?.trim()) form.append('note', note.trim());
    const res = await apiClient.post(`${P}/${token}/quote-files`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300_000 });
    return res.data?.data;
  },

  /** A quotation file this supplier sent, to view it again. */
  async quoteFile(token: string, documentId: string): Promise<Blob> {
    const res = await apiClient.get(`${P}/${token}/quote-files/${documentId}`, { responseType: 'blob' });
    return res.data;
  },

  /** Submit a structured quote. */
  async submitQuote(token: string, dto: PortalQuoteDto): Promise<{ rfqId: string; rfqNumber: string; status: string }> {
    const res = await apiClient.post(`${P}/${token}/quote`, dto);
    return res.data?.data;
  },
};

export default rfqPortalService;
